import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

import type { LensSpanProps } from '../../types';
import { getColorValue } from '../../utilities';

const speed = 1;
const dotsAmount = 8;

const sizes = {
  small: {
    totalSize: 18,
  },
  medium: {
    totalSize: 24,
  },
  large: {
    totalSize: 48,
  },
};

const getDotSize = props => {
  return sizes[props.size].totalSize / 6;
};

const getTotalSize = props => {
  return sizes[props.size].totalSize;
};

const dotScale = keyframes`
  50% {
    transform: scale(1);
  }
`;

const LoaderWrapper = styled.span<LoaderWrapperProps>`
  display: inline-block;
  vertical-align: middle;
  height: ${props => getTotalSize(props)}px;
  width: ${props => getTotalSize(props)}px;
`;

const Dots = styled.span`
  display: grid;
  grid-template-areas: 'stack';
  height: 100%;
  width: 100%;
`;

const DotWrapper = styled.span<DotProps>`
  grid-area: stack;
  place-self: center;
  transform: rotate(${props => props.position * (360 / dotsAmount)}deg)
    translateY(${props => getTotalSize(props) / 2 - getDotSize(props) / 2}px);

  &:after {
    content: '';
    height: ${props => getDotSize(props)}px;
    width: ${props => getDotSize(props)}px;
    border-radius: ${props => getDotSize(props)}px;
    background-color: ${props => getColorValue(props.color)};
    display: block;
    transform: scale(0.65);
    animation: ${dotScale} ${speed}s
      ${props => (props.position * speed) / dotsAmount}s ease-in-out infinite;
  }
`;

const Dot = ({ position, color, size }: DotProps) => (
  <DotWrapper color={color} position={position} size={size} />
);

const Loader = ({
  color = 'body',
  size = 'medium',
  ...props
}: LoaderProps &
  React.ComponentProps<typeof LoaderWrapper> &
  Omit<
    LensSpanProps,
    keyof LoaderProps & React.ComponentProps<typeof LoaderWrapper>
  >) => {
  let i;
  const dotsList = [];

  for (i = 0; i < dotsAmount; i++) {
    dotsList.push(<Dot color={color} position={i} size={size} key={i} />);
  }

  return (
    <LoaderWrapper size={size} {...props}>
      <Dots>{dotsList}</Dots>
    </LoaderWrapper>
  );
};

export const availableSizes = Object.keys(sizes);

type LoaderProps = {
  color?: string;
  size?: 'small' | 'medium' | 'large';
};

type LoaderWrapperProps = {
  size?: string;
};

type DotProps = {
  position?: number;
  color?: string;
  size?: string;
};

export default Loader;
